<template>
    <app-navbar :logo-url="urlGenerator('/vendor/dashboard')"
                :user="navbarUser"
                :selected-language="userLanguage"
                :language-data="languageList"
                :notificationData="notificationList"
                :profile-data="profileData"
                :showIdentifier="showIdentifier"
                :totalUnread="totalUnread"
                :all-notification-url="urlGenerator('/all-notifications')"
                @markAllAsRead="markAllAsRead"
                @clicked="readNotification"/>
</template>

<script>
    import {UserMixin} from '../Auth/Mixins/UserMixin';
    import * as actions from '../../../Config/ApiUrl';
    import {axiosPost, urlGenerator, externalApiCall} from "../../../Helpers/AxiosHelper";
    import {mapActions} from "vuex";
    import AppFunction from "../../../../core/helpers/app/AppFunction";

    export default {
        name : 'DashboardNavbar',
        mixins: [UserMixin],
        props: {
            // This is passed from Blade as :user="{{ json_encode(session('vendor_user')) }}"
            user: {
                type: Object,
                default: () => ({})
            },
            logo: {
                type: String,
                default: '/images/icon.png'
            },
            logoutUrl: {
                type: String,
                default: null
            }
        },
        data(){
            return{
                urlGenerator,
                fcmService: null
            }
        },
        computed: {
            navbarUser() {
                // Determine the best source for user info
                // First try Vuex store, then fallback to prop from Blade
                const storeUser = this.user && this.user.loggedInUser ? this.user.loggedInUser : {};
                
                // If store user is empty but we have a prop user, use that
                if (_.isEmpty(storeUser) || !storeUser.name) {
                    // Extract user data from potential vendor structure
                    const propVal = this.$props.user || {};
                    const userData = propVal.service_provider || propVal.user || propVal.data || propVal;
                    
                    if (userData && (userData.name || userData.full_name)) {
                        return {
                            ...userData,
                            full_name: userData.full_name || userData.name,
                            img: userData.image_url || userData.profile_picture?.full_url || userData.profile_picture || urlGenerator('/images/avatar-demo.jpg'),
                            role: userData.role || (userData.roles && userData.roles.length ? userData.roles[0].name : '')
                        };
                    }
                }
                
                return storeUser;
            },
            profileData() {
                // Use logoutUrl prop if provided, otherwise fall back to default
                const logoutUrl = this.logoutUrl || urlGenerator(actions.LOGOUT);
                
                // For vendor, use vendor profile route, otherwise use my-profile
                // Check if we're in vendor context by checking if logoutUrl contains 'vendor'
                const isVendor = logoutUrl && logoutUrl.includes('vendor');
                const profileUrl = isVendor 
                    ? urlGenerator('/vendor/profile')
                    : urlGenerator(actions.MY_PROFILE);
                
                return [
                    {
                        optionName: this.$t('custom.my_profile'),
                        optionIcon: 'user',
                        url: profileUrl
                    },
                    {
                        optionName: this.$t('custom.logout'),
                        optionIcon: 'log-out',
                        url: logoutUrl
                    },
                ]
            },
            languageList() {
                return this.$store.state.settings.languages
            },
            userLanguage() {
                const lang = window.localStorage.getItem('app-language');
                return lang ? lang.toUpperCase() : 'EN';
            },
            showIdentifier() {
                const notifications = this.$store.getters.notificationList;
                return !(notifications && notifications.length && notifications.total_unread) ? false : true;
            },
            totalUnread() {
                const notifications = this.$store.getters.notificationList;
                return notifications?.total_unread || 0;
            }
        },
        async created() {
            console.log('DashboardNavbar: Component created');
            
            // Debug: Log meta tags
            console.log('API Token Meta:', document.querySelector('meta[name="api-token"]')?.getAttribute('content') ? 'FOUND' : 'MISSING');
            console.log('API URL Meta:', document.querySelector('meta[name="rawafed-api-url"]')?.getAttribute('content'));
            
            this.$store.dispatch('getSettings');
            this.$store.dispatch('getNotifications');
            this.$store.dispatch('setSelectedLanguage');
            this.$store.dispatch('getUser');
            this.$store.dispatch('getLanguages');
            
            // Initialize FCM if user is authenticated
            // Wait a bit for store to be ready
            setTimeout(async () => {
                try {
                    console.log('FCM: Starting initialization...');
                    
                    // IMPORTANT: Register service worker FIRST before initializing FCM
                    // This ensures the service worker is ready when we try to get the token
                    console.log('FCM: Registering service worker first...');
                    await this.registerServiceWorker();
                    
                    // Wait a bit for service worker to activate
                    await new Promise(resolve => setTimeout(resolve, 500));
                    
                    const FcmService = (await import('../../../Services/FcmService.js')).default;
                    const initialized = await FcmService.initialize();
                    console.log('FCM: Initialization result:', initialized);
                    
                    if (initialized) {
                        console.log('FCM: Initialized successfully, registering token...');
                        // Register token (this will wait for service worker if needed)
                        const registered = await FcmService.registerToken();
                        console.log('FCM: Token registration result:', registered);
                        
                        // Setup message handler to refresh notifications
                        FcmService.onMessage((payload) => {
                            console.log('FCM: Custom message handler triggered');
                            console.log('FCM: Payload in custom handler:', payload);
                            // Refresh notifications when message received
                            this.$store.dispatch('getNotifications');
                        });
                        
                        // Listen for messages from service worker (for background notifications)
                        if ('serviceWorker' in navigator) {
                            navigator.serviceWorker.addEventListener('message', (event) => {
                                if (event.data && event.data.type === 'FCM_NOTIFICATION_RECEIVED') {
                                    console.log('FCM: Notification received in background, refreshing list...');
                                    this.$store.dispatch('getNotifications');
                                } else if (event.data && event.data.type === 'FCM_NOTIFICATION_CLICKED') {
                                    console.log('FCM: Notification clicked, handling...');
                                    // Handle notification click if needed
                                    if (event.data.data && event.data.data.deep_link) {
                                        // Handle deep link
                                        console.log('FCM: Deep link:', event.data.data.deep_link);
                                    }
                                    this.$store.dispatch('getNotifications');
                                }
                            });
                        }
                        
                        // Store token globally for debugging
                        window.currentFCMToken = await FcmService.getToken();
                        console.log('FCM: Token stored in window.currentFCMToken for debugging');
                        
                        // Store FCM service reference for logout
                        this.fcmService = FcmService;
                    } else {
                        console.warn('FCM: Initialization failed - check notification permission and Firebase settings');
                    }
                } catch (error) {
                    console.error('FCM: Failed to initialize:', error);
                    console.error('FCM: Error details:', error.message, error.stack);
                }
            }, 1000); // Wait 1 second for store to initialize
        },
        beforeDestroy() {
            // Unregister FCM token on logout/component destroy
            if (this.fcmService) {
                this.fcmService.unregisterToken().catch(error => {
                    console.warn('FCM: Failed to unregister token on logout:', error);
                });
            }
        },

        methods: {
            ...mapActions([
                'getNotifications'
            ]),
            readNotification(notification) {
                // If it's already read, just navigate
                if (notification.status !== this.$t('new')) {
                    if (notification.url && notification.url !== '#') {
                        window.location = notification.url;
                    }
                    return;
                }
                externalApiCall('POST', `/api/notifications/${notification.id}/read`).then(({data}) => {
                    if (notification.url && notification.url !== '#') {
                        window.location = notification.url;
                    } else {
                        this.getNotifications();
                    }
                }).catch(() => {
                    if (notification.url && notification.url !== '#') {
                        window.location = notification.url;
                    }
                });
            },
            markAllAsRead() {
                externalApiCall('POST', '/api/notifications/mark-all-read').then(() => {
                    this.getNotifications();
                }).catch(error => console.warn('Failed to mark all as read:', error));
            },
            async registerServiceWorker() {
                if ('serviceWorker' in navigator) {
                    try {
                        // Check if service worker is already registered
                        let registration = await navigator.serviceWorker.getRegistration();
                        
                        if (!registration) {
                            // Register service worker
                            registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
                            console.log('Service Worker registered:', registration);
                        } else {
                            console.log('Service Worker already registered:', registration);
                        }
                        
                        // Wait for service worker to be active
                        if (registration.installing) {
                            console.log('Service Worker installing, waiting for activation...');
                            await new Promise((resolve) => {
                                registration.installing.addEventListener('statechange', function handler() {
                                    if (registration.installing.state === 'activated') {
                                        registration.installing.removeEventListener('statechange', handler);
                                        resolve();
                                    }
                                });
                            });
                        } else if (registration.waiting) {
                            console.log('Service Worker waiting, activating...');
                            registration.waiting.postMessage({ type: 'SKIP_WAITING' });
                            await new Promise((resolve) => {
                                registration.waiting.addEventListener('statechange', function handler() {
                                    if (registration.waiting.state === 'activated') {
                                        registration.waiting.removeEventListener('statechange', handler);
                                        resolve();
                                    }
                                });
                            });
                        }
                        
                        // Ensure service worker is active
                        if (registration.active) {
                            console.log('Service Worker is active');
                            
                            // Send Firebase config to service worker
                            const { loadFirebaseConfig } = await import('../../../Config/firebase.js');
                            const config = await loadFirebaseConfig();
                            
                            if (config) {
                                registration.active.postMessage({
                                    type: 'FIREBASE_CONFIG',
                                    config: config
                                });
                                console.log('Firebase config sent to service worker');
                            }
                        } else {
                            console.warn('Service Worker not active yet');
                        }
                        
                        return registration;
                    } catch (error) {
                        console.warn('Service Worker registration failed:', error);
                        return null;
                    }
                } else {
                    console.warn('Service Worker not supported in this browser');
                    return null;
                }
            }
        }
    }
</script>
